import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import logo from '../assets/logo.png';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from 'react-icons/fa'; // Only import necessary icons

// Define scroll threshold for header becoming sticky
const stickyThreshold = 10;

// Pre-calculate initial header state outside the component for first render accuracy
const initialIsMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
const initialIsSticky = typeof window !== 'undefined' ? window.scrollY > stickyThreshold : false;

const Header = () => {
  const [isMobile, setIsMobile] = useState(initialIsMobile);
  const [isSticky, setIsSticky] = useState(initialIsSticky);
  const [menuOpen, setMenuOpen] = useState(false); // Mobile menu state
  const [hoveredLink, setHoveredLink] = useState(null); // Desktop link hover state

  const headerRef = useRef(null); // Ref for the header element
  const [headerActualHeight, setHeaderActualHeight] = useState(0); // State for actual header height

  useEffect(() => {
    const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
        if (headerRef.current) {
            setHeaderActualHeight(headerRef.current.offsetHeight);
        }
    };
    const handleScroll = () => {
        setIsSticky(window.scrollY > stickyThreshold);
        if (headerRef.current) {
            setHeaderActualHeight(headerRef.current.offsetHeight);
        }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);

    if (typeof window !== 'undefined') {
        handleResize();
        handleScroll();
    }

    // Handle body scroll when mobile menu is open
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      document.documentElement.style.overflow = "unset";
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      document.body.style.overflow = "unset"; // Clean up on unmount
      document.documentElement.style.overflow = "unset"; // Clean up on unmount
    };
  }, [menuOpen]); // Re-run effect when menuOpen changes to control body scroll

  useEffect(() => {
    if (headerRef.current) {
      setHeaderActualHeight(headerRef.current.offsetHeight);
    }
  }, [menuOpen, isSticky]);


  const handleLinkClick = () => setMenuOpen(false);

  // Define nav items (common for both views)
  const navItems = [
    { text: 'HOME', path: '/' },
    { text: 'ABOUT US', path: '/#about-us' },
    { text: 'MENU', path: '/menu' },
    { text: 'SERVICES', path: '/#services' },
    { text: 'CONTACT US', path: '/contactus' },
  ];

  // Dynamically determine header background and shadow
  let currentHeaderBgColor;
  let currentHeaderBoxShadow;
  let currentHeaderTopPosition;

  if (isMobile) {
    // --- MOBILE SPECIFIC LOGIC ---
    // If menu is open, header background is always transparent black (highest priority)
    if (menuOpen) {
      currentHeaderBgColor = 'rgba(0,0,0,0.95)'; // Consistent with drawer background
      currentHeaderBoxShadow = 'none';
    } else if (isSticky) { // If menu is closed AND scrolled, header is white
        currentHeaderBgColor = 'white';
        currentHeaderBoxShadow = '0 6px 20px rgba(0,0,0,0.15)';
    } else { // Otherwise (menu closed and not sticky), it's always transparent
      currentHeaderBgColor = 'transparent';
      currentHeaderBoxShadow = 'none';
    }

    currentHeaderTopPosition = '10px';
    // Header moves to top (0px) when sticky OR menu is open
    if (isSticky || menuOpen) {
        currentHeaderTopPosition = '0px';
    }
  } else {
    // --- DESKTOP SPECIFIC LOGIC ---
      currentHeaderBgColor = 'white'; // Always white on desktop
      if (isSticky) {
          currentHeaderBoxShadow = '0 6px 20px rgba(0,0,0,0.15)';
          currentHeaderTopPosition = '0px';
      } else {
          currentHeaderBoxShadow = '0 4px 12px rgba(0,0,0,0.05)';
          currentHeaderTopPosition = '20px';
      }
  }

  // Common header wrapper styles (fixed position, centering)
  const commonHeaderWrapperStyle = {
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    borderRadius: '0px',
    transition: 'top 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease',
    // Dynamic styles
    backgroundColor: currentHeaderBgColor,
    boxShadow: currentHeaderBoxShadow,
    top: currentHeaderTopPosition,
  };


  // --- DESKTOP VIEW JSX ---
  const DesktopHeader = () => (
    <header style={{
        ...commonHeaderWrapperStyle,
        width: '75%',
        maxWidth: 1200,
        padding: '10px 30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'nowrap',
    }}>
      {/* Logo */}
      <div>
        <Link to="/">
          <img src={logo} alt="Logo" style={{ height: 60, width: 'auto' }}/>
        </Link>
      </div>

      {/* Navigation */}
      <nav style={navDesktop}>
        {navItems.map((item, i) => {
          // Use Link for internal routes without hash, HashLink for routes with hash
          const NavComponent = item.path.includes('#') ? HashLink : Link;
          return (
            <NavComponent
              key={i}
              to={item.path}
              style={{
                  ...navLink,
                  color: hoveredLink === i ? '#D4A762' : '#333',
                  borderBottom: hoveredLink === i ? '2px solid #D4A762' : 'none',
                  padding: '10px 0',
                  textAlign: 'left',
              }}
              onMouseEnter={() => setHoveredLink(i)}
              onMouseLeave={() => setHoveredLink(null)}
              smooth={item.path.includes('#')} // Enable smooth scroll for HashLink
            >
              {item.text}
            </NavComponent>
          );
        })}
      </nav>

      {/* Book a Table Button */}
      <HashLink
        to="/#contact-us"
        className="book-table-btn"
        smooth
      >
        <span className="book-table-btn-icon"> </span>
        <span className="book-table-btn-text">Book A Table</span>
      </HashLink>
    </header>
  );


  // --- MOBILE VIEW JSX ---
  const MobileHeader = () => {
    // Dynamically define navMobileOpenStyles inside the component
    // This allows access to headerActualHeight and currentHeaderTopPosition
    const navMobileOpenStyles = {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(0,0,0,0.95)', // Near-solid black background for the drawer
        padding: '0 0 10px 0',
        borderRadius: '0',
        boxShadow: '0 6px 20px rgba(0,0,0,0.1)',
        // Calculate height to fill remaining viewport space below the header
        height: `calc(100vh - ${headerActualHeight + (parseInt(currentHeaderTopPosition) || 0)}px)`,
        overflowY: 'auto', // Enable scrolling if content overflows
        // Align content to the top
        justifyContent: 'flex-start',
        alignItems: 'center', // Align children (nav links, contact section) to center
    };

    return (
      <header ref={headerRef} style={{ // Assign ref here to measure actual height
          ...commonHeaderWrapperStyle,
          width: '100%',
          maxWidth: 'none',
          padding: '10px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          borderRadius: '0',
      }}>
        {/* Logo */}
        <div>
          <Link to="/">
            {/* Image height set to 60px for small screen */}
            <img src={logo} alt="Logo" style={{ height: 60, width: 'auto', maxWidth: 'none' }}/>
          </Link>
        </div>

        {/* Hamburger */}
        <div
          style={{
            ...hamburgerContainer,
            transform: menuOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            marginRight: '5px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {/* Hamburger lines now use dynamic color based on sticky/menuOpen state */}
          <div style={{ ...getHamburgerLineStyle(isSticky, menuOpen), transform: menuOpen ? 'rotate(45deg) translateY(6px)' : 'rotate(0)' }} />
          <div style={{ ...getHamburgerLineStyle(isSticky, menuOpen), opacity: menuOpen ? 0 : 1 }} />
         <div style={{ ...getHamburgerLineStyle(isSticky, menuOpen), transform: menuOpen ? 'rotate(-45deg) translateY(-6px)' : 'rotate(0)' }} />
        </div>

        {/* Mobile Navigation (Overlay) */}
        <nav
          style={{
              ...navBase,
              ...navMobile,
              // Use dynamic navMobileOpenStyles
              ...(menuOpen ? navMobileOpenStyles : navMobileClosed),
              width: '100%',
              // Top position remains the same, calculated in commonHeaderWrapperStyle
              top: `${headerActualHeight + (parseInt(currentHeaderTopPosition) || 0)}px`,
              borderRadius: '0',
          }}
        >
          {navItems.map((item, i) => {
            const NavComponent = item.path.includes('#') ? HashLink : Link;
            return (
              <NavComponent
                key={i}
                to={item.path}
                // REMOVED onMouseEnter and onMouseLeave for mobile view
                style={{
                  ...navLink,
                  // Mobile links are always white, no hover color
                  color: 'white',
                  borderBottom: 'none',
                  padding: '12px 0',
                  width: '100%',
                  textAlign: 'center',
                  fontSize: '16.8px',
                }}
                onClick={handleLinkClick}
                smooth={item.path.includes('#')}
              >
                {item.text}
              </NavComponent>
            );
          })}
        </nav>
      </header>
    );
  };

  // --- RENDER BASED ON isMobile STATE ---
  return (
    <>
      {/* --- INLINE STYLES FOR BUTTONS AND MOBILE ICONS --- */}
      <style>
        {`
        .book-table-btn {
            /* Base styles for the button */
            background-color: #D6A54F; /* Initial background color */
            font-family: "Hanken Grotesk", Sans-serif;
            font-size: 14px;
            font-weight: bold;
            line-height: 1em;
            color: #FFFFFF; /* Initial text color is white */
            border-radius: 25px;
            padding: 13px 20px;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            position: relative;
            overflow: hidden;
            z-index: 1;
            transition: color 0.3s ease, background-color 0.3s ease; /* Transition for properties on the button itself */
            white-space: nowrap; /* Prevent text wrapping */
        }

        /* Styles for the button text and icon to keep them above the pseudo-element */
        .book-table-btn-text,
        .book-table-btn-icon {
            position: relative;
            z-index: 2;
        }

        /* Styles for the SVG icon (if present and used) */
        .book-table-btn-icon {
            display: flex;
            align-items: center;
            margin-right: 8px; /* Space between icon and text */
        }

        .book-table-btn-icon svg {
            width: 13px;
            height: 10px;
            stroke: currentColor; /* Inherit color from parent */
        }

        /* Pseudo-element for the hover effect (sweeping background) */
        .book-table-btn::before {
            content: '';
            display: block;
            position: absolute;
            top: 0;
            bottom: 0;
            left: -15%; /* Start slightly off-left to make the sweep appear from outside */
            right: 0;
            width: 0;
            height: 106%; /* Slightly larger to cover border-radius edges */
            background: #C08E3B; /* The lighter color that sweeps in */
            transform: skew(-25deg); /* Skew for a sweeping angle */
            transition: width 0.4s ease-in-out; /* Transition for the sweep */
            z-index: -1; /* Place behind the button content */
            border-radius: 25px; /* Match button's border-radius */
        }

        /* Hover effect on the button itself */
        .book-table-btn:hover {
            color: #FFFFFF; /* Changed: Text color remains white on hover */
            background-color: transparent; /* Makes the pseudo-element visible */
        }

        /* Hover effect for the pseudo-element (the sweep) */
        .book-table-btn:hover::before {
            width: 130%; /* Triggers the sweep */
        }

        /* --- MOBILE MENU ICONS STYLES --- */

        /* Container for each contact item (icon + text) for group hover */
        .mobile-contact-item-group {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 5px;
            font-size: 15px;
            line-height: 1.4;
            width: 100%;
            padding: 8px 0px;
            box-sizing: border-box;
            cursor: pointer; /* Indicate it's clickable */
            transition: transform 0.3s ease; /* For the scale effect on the whole group */
        }

        .mobile-contact-item-group:hover {
            transform: scale(1.05); /* Slight scale up on hover */
        }

        /* Base style for the circular background of the icons */
        .mobile-icon-circle {
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 45px;
            height: 45px;
            border-radius: 50%;
            background-color: white; /* Default white background */
            text-decoration: none;
            flex-shrink: 0;
            margin-bottom: 5px;
            transition: background-color 0.3s ease, transform 0.3s ease; /* Smooth transitions */
        }

        /* Hover effect for the circular icon background */
        .mobile-contact-item-group:hover .mobile-icon-circle {
            background-color: #D4A762; /* Accent color on hover */
        }

        /* Style for the icon itself inside the circle */
        .mobile-icon-circle-icon {
            color: black; /* Default black icon color */
            font-size: 20px; /* Adjust as needed */
            transition: color 0.3s ease; /* Smooth transition for icon color */
        }

        /* Hover effect for the icon color */
        .mobile-contact-item-group:hover .mobile-icon-circle-icon {
            color: white; /* White icon on hover */
        }

        /* Style for the "Location", "Phone Number", "Email Address" labels */
        .mobile-icon-text-label {
            font-size: 14px;
            font-weight: bold;
            color: #CCC; /* Default light gray color */
            margin-bottom: 2px;
            line-height: 1.2;
            transition: color 0.3s ease; /* Smooth transition for label color */
        }

        /* Hover effect for the labels */
        .mobile-contact-item-group:hover .mobile-icon-text-label {
            color: #D4A762; /* Accent color on hover */
        }


        /* --- MOBILE SOCIAL MEDIA ICONS STYLES --- */
        .mobile-social-icon-circle {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 45px; /* Fixed width */
            height: 45px; /* Fixed height */
            border-radius: 50%;
            background-color: white; /* Default white background */
            text-decoration: none;
            transition: background-color 0.3s ease, transform 0.3s ease; /* Smooth transitions */
        }

        .mobile-social-icon-circle:hover {
            background-color: #D4A762; /* Accent color on hover */
            transform: translateY(-2px) scale(1.05); /* Slight lift and scale */
        }

        .mobile-social-icon-inner {
            color: black; /* Default black icon color */
            font-size: 20px; /* Adjust as needed */
            transition: color 0.3s ease; /* Smooth transition for icon color */
        }

        .mobile-social-icon-circle:hover .mobile-social-icon-inner {
            color: white; /* White icon on hover */
        }
        `}
      </style>
      {isMobile ? <MobileHeader /> : <DesktopHeader />}
    </>
  );
};


// ==== COMMON STYLES (shared between mobile and desktop if applicable) ====

const navBase = {
  alignItems: 'center',
  transition: 'all 0.3s ease',
};

const navDesktop = {
  display: 'flex',
  gap: '25px',
};

const navMobile = {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  width: '100%',
  minWidth: '280px',
  boxSizing: 'border-box',
};

const navMobileClosed = {
  display: 'none',
};

const navLink = {
  textDecoration: 'none',
  fontWeight: '500',
  fontSize: '14px',
  position: 'relative',
  transition: 'color 0.3s ease, border-bottom 0.3s ease',
};

const hamburgerContainer = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '10px',
  gap: '5px',
  transition: 'transform 0.3s ease',
  backgroundColor: 'transparent',
  boxShadow: 'none',
};

const getHamburgerLineStyle = (isSticky, menuOpen) => ({
  width: '25px',
  height: '3px',
  backgroundColor: (isSticky && !menuOpen) ? '#333' : '#FFFFFF',
  borderRadius: '2px',
  transition: 'all 0.3s ease',
});

// --- RETAINED INLINE STYLES FOR MOBILE CONTACT INFO SECTION LAYOUT ---
// These styles are no longer directly used in the JSX as the "Get In Touch" section is removed,
// but are kept here for completeness of the original style definitions if they were referenced elsewhere.
const mobileContactSectionStyle = {
  marginTop: '20px',
  padding: '0 20px',
  width: '100%',
  maxWidth: '400px',
  backgroundColor: 'transparent',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px', // Space between contact items
  color: 'white',
  boxSizing: 'border-box',
  alignSelf: 'center',
  alignItems: 'center',
};

const mobileContactHeadingStyle = {
  fontSize: '20px',
  fontWeight: 'bold',
  marginBottom: '10px',
  textAlign: 'center',
  color: '#D4A762',
};

const mobileContactInfoStyle = {
    fontSize: '15px',
    lineHeight: '1.4',
    color: 'white', // Ensure this remains white
    margin: 0,
    wordBreak: 'break-word',
};


export default Header;